import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import { updateProfile } from "../store/slices/authSlice";

const MyProfile = () => {
    const dispatch = useDispatch();
    const { user, isLoading } = useSelector((state) => state.auth);

    const [form, setForm] = useState({
        name: "",
        current_password: "",
        password: "",
        password_confirmation: "",
    });
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState({
        current_password: false,
        password: false,
        password_confirmation: false,
    });

    // Prefill the name from the logged-in user.
    useEffect(() => {
        if (user?.name) {
            setForm((prev) => ({ ...prev, name: user.name }));
        }
    }, [user?.name]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const toggleShow = (field) =>
        setShow((prev) => ({ ...prev, [field]: !prev[field] }));

    const validateLocal = () => {
        const err = {};
        if (!form.name.trim()) err.name = "Name is required.";

        const wantsPasswordChange =
            form.current_password || form.password || form.password_confirmation;

        if (wantsPasswordChange) {
            if (!form.current_password) err.current_password = "Enter your current password.";
            if (!form.password) err.password = "Enter a new password.";
            else if (form.password.length < 8) err.password = "New password must be at least 8 characters.";
            if (form.password !== form.password_confirmation)
                err.password_confirmation = "Passwords do not match.";
        }
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateLocal()) return;

        const payload = { name: form.name.trim() };
        if (form.current_password || form.password || form.password_confirmation) {
            payload.current_password = form.current_password;
            payload.password = form.password;
            payload.password_confirmation = form.password_confirmation;
        }

        try {
            await dispatch(updateProfile(payload)).unwrap();
            Swal.fire("Saved", "Your profile has been updated.", "success");
            // Clear password fields after a successful save.
            setForm((prev) => ({
                ...prev,
                current_password: "",
                password: "",
                password_confirmation: "",
            }));
            setErrors({});
        } catch (rejected) {
            // Backend returns a { field: [messages] } object on 422.
            if (rejected && typeof rejected === "object") {
                const mapped = {};
                Object.keys(rejected).forEach((k) => {
                    mapped[k] = Array.isArray(rejected[k]) ? rejected[k][0] : rejected[k];
                });
                setErrors(mapped);
                const firstMsg = Object.values(mapped)[0];
                Swal.fire("Error", firstMsg || "Please fix the highlighted fields.", "error");
            } else {
                Swal.fire("Error", rejected || "Failed to update profile.", "error");
            }
        }
    };

    // Reusable password field with a show/hide eye toggle.
    // NOTE: this is a plain render function (called inline), NOT a nested React
    // component — a nested component would remount each keystroke and drop focus.
    const renderPasswordField = ({ label, name, placeholder, autoComplete }) => (
        <div className="mb-3">
            <label className="form-label fw-semibold">{label}</label>
            <div className="position-relative">
                <input
                    type={show[name] ? "text" : "password"}
                    name={name}
                    autoComplete={autoComplete}
                    className={`form-control ${errors[name] ? "is-invalid" : ""}`}
                    value={form[name]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    style={{ paddingRight: "2.5rem" }}
                />
                {/* Eye toggle button */}
                <span
                    className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary"
                    style={{ cursor: "pointer", zIndex: 5 }}
                    title={show[name] ? "Hide password" : "Show password"}
                    onClick={() => toggleShow(name)}
                >
                    <i className={show[name] ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </span>
                {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
            </div>
        </div>
    );

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white border-0 pb-0">
                        <h5 className="mb-1 fw-bold">My Profile</h5>
                        <div className="alert alert-info d-flex align-items-center mt-3 mb-0" role="alert">
                            <i className="ri-information-line me-2 fs-5"></i>
                            <span>Here you can update your name and password.</span>
                        </div>
                    </div>

                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit} noValidate>
                            <div className="row g-4">
                                {/* Name */}
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Your name"
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>

                                {/* Email (read-only, not editable) */}
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Email</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={user?.email || ""}
                                        readOnly
                                        disabled
                                    />
                                    <small className="text-muted">Email cannot be changed here.</small>
                                </div>
                            </div>

                            <hr className="my-4" />
                            <p className="text-muted mb-3">
                                <i className="ri-lock-2-line me-1"></i>
                                Change Password <span className="fw-normal">(leave blank to keep your current password)</span>
                            </p>

                            <div className="row g-3">
                                <div className="col-md-4">
                                    {renderPasswordField({ label: "Current Password", name: "current_password", placeholder: "Enter current password", autoComplete: "current-password" })}
                                </div>
                                <div className="col-md-4">
                                    {renderPasswordField({ label: "New Password", name: "password", placeholder: "At least 8 characters", autoComplete: "new-password" })}
                                </div>
                                <div className="col-md-4">
                                    {renderPasswordField({ label: "Confirm New Password", name: "password_confirmation", placeholder: "Re-enter new password", autoComplete: "new-password" })}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end mt-4">
                                <button type="submit" className="btn btn-primary px-4" disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-save-line me-1"></i> Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default MyProfile;
